import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import { confirmAsync } from "../auth";
import {
  EmptyState,
  ErrorBox,
  SkeletonRows,
  StatusPill,
} from "../components/States";
import { PullToRefresh } from "../components/PullToRefresh";
import { fmtMoney } from "../format";
import { MeResponse } from "../types";

interface Balance {
  rate_fixed: boolean;
  expected: number;
  rub_body: number;
  rub_commission: number;
  received: number;
  debit: number;
  credit: number;
  fully_covered: boolean;
}
interface DealLine {
  deal_id: number;
  counterparty_id: number | null;
  counterparty_name: string | null;
  company_name: string | null;
  currency: string;
  amount: number;
  status: string;
  deal_date: string | null;
  balance: Balance;
}
interface Inflow {
  id: number;
  amount: number;
  note: string | null;
  received_at: string | null;
}
interface JournalRow {
  id: number;
  deal_id: number;
  counterparty_name: string | null;
  amount: number;
  note: string | null;
  received_at: string | null;
  recorded_by_name: string | null;
}
interface DebtorRow {
  counterparty_id: number | null;
  counterparty_name: string | null;
  total_debit: number;
  total_credit: number;
  deal_count: number;
}

function fmtDT(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ""
    : d.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const VIEW_LABELS: Record<string, string> = {
  deals: "Заявки",
  debtors: "Должники",
  journal: "Журнал",
};

/** Учёт поступлений рублей: дебиторка/кредиторка по заявкам + журнал. */
export function RubInflowPage({ me }: { me: MeResponse }) {
  const [view, setView] = useState<"deals" | "debtors" | "journal">("deals");
  const canRecord =
    me.permissions.payments_confirm || me.permissions.payments_write;

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">💰 Поступления рублей</h1>
      <div className="flex gap-1 p-1 bg-tg-secondaryBg rounded-xl text-sm">
        {(["deals", "debtors", "journal"] as const).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`flex-1 py-1.5 rounded-lg ${
              view === v ? "bg-tg-button text-tg-buttonText" : "text-tg-hint"
            }`}
          >
            {VIEW_LABELS[v]}
          </button>
        ))}
      </div>
      {view === "deals" && <DealsView canRecord={canRecord} />}
      {view === "debtors" && <DebtorsView />}
      {view === "journal" && <JournalView />}
    </div>
  );
}

function DealsView({ canRecord }: { canRecord: boolean }) {
  const [deals, setDeals] = useState<DealLine[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = async () => {
    setError(null);
    try {
      setDeals(await api.get<DealLine[]>("/api/rub_inflows/deals"));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const totalDebit = (deals || []).reduce((s, d) => s + d.balance.debit, 0);
  const totalCredit = (deals || []).reduce(
    (s, d) => s + d.balance.credit,
    0,
  );

  return (
    <PullToRefresh onRefresh={load}>
      <div className="space-y-3">
        <div className="text-xs text-tg-hint">
          Учёт частичных поступлений базовой валюты по заявкам. При
          полном покрытии заявка закрывается автоматически.
        </div>
        {error && <ErrorBox text={error} />}
        {!deals && !error && <SkeletonRows rows={5} />}

        {deals && (deals.length > 0 || totalDebit > 0) && (
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-tg-sectionBg rounded-xl p-3">
              <div className="text-lg font-bold tabular-nums">
                {fmtMoney(totalDebit)} ₽
              </div>
              <div className="text-xs text-tg-hint">дебиторка всего</div>
            </div>
            <div className="bg-tg-sectionBg rounded-xl p-3">
              <div className="text-lg font-bold tabular-nums">
                {fmtMoney(totalCredit)} ₽
              </div>
              <div className="text-xs text-tg-hint">кредиторка всего</div>
            </div>
          </div>
        )}

        {deals && deals.length === 0 && (
          <EmptyState
            icon="✅"
            text="Нет заявок с незакрытым балансом по рублям."
          />
        )}

        {deals &&
          deals.map((d) => (
            <DealRow
              key={d.deal_id}
              line={d}
              open={openId === d.deal_id}
              canRecord={canRecord}
              onToggle={() =>
                setOpenId(openId === d.deal_id ? null : d.deal_id)
              }
              onChanged={load}
            />
          ))}
      </div>
    </PullToRefresh>
  );
}

function DealRow({
  line,
  open,
  canRecord,
  onToggle,
  onChanged,
}: {
  line: DealLine;
  open: boolean;
  canRecord: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  const b = line.balance;
  return (
    <div className="bg-tg-sectionBg rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full text-left px-4 py-3 text-sm"
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-medium">
            №{line.deal_id} · {line.counterparty_name || "—"}
          </span>
          {b.fully_covered ? (
            <StatusPill tone="success">оплачено</StatusPill>
          ) : b.debit > 0 ? (
            <StatusPill tone="danger">дебиторка</StatusPill>
          ) : b.credit > 0 ? (
            <StatusPill tone="warning">переплата</StatusPill>
          ) : (
            <StatusPill tone="neutral">{line.status}</StatusPill>
          )}
        </div>
        <div className="mt-1 text-xs text-tg-hint">
          Ожидается {fmtMoney(b.expected)} ₽ · поступило{" "}
          {fmtMoney(b.received)} ₽
          {b.debit > 0 && ` · остаток ${fmtMoney(b.debit)} ₽`}
          {b.credit > 0 && ` · переплата ${fmtMoney(b.credit)} ₽`}
        </div>
      </button>
      {open && (
        <DealDetail
          dealId={line.deal_id}
          canRecord={canRecord}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

function DealDetail({
  dealId,
  canRecord,
  onChanged,
}: {
  dealId: number;
  canRecord: boolean;
  onChanged: () => void;
}) {
  const [data, setData] = useState<{
    balance: Balance;
    inflows: Inflow[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setData(
        await api.get<{ balance: Balance; inflows: Inflow[] }>(
          `/api/rub_inflows/deal/${dealId}`,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const record = async () => {
    const v = Number(amount.replace(",", ".").replace(/\s/g, ""));
    if (!(v > 0)) {
      setError("Введите сумму поступления больше нуля.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/rub_inflows", { deal_id: dealId, amount: v });
      setAmount("");
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const removeInflow = async (id: number) => {
    if (!(await confirmAsync("Удалить эту запись поступления?"))) return;
    setError(null);
    try {
      await api.del(`/api/rub_inflows/${id}`);
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  if (!data) {
    return (
      <div className="px-4 pb-3">
        <div className="text-xs text-tg-hint">Загрузка…</div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-3 border-t border-tg-secondaryBg pt-2 space-y-2">
      <dl className="text-xs grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5">
        <dt className="text-tg-hint">Тело платежа</dt>
        <dd className="text-right tabular-nums">
          {fmtMoney(data.balance.rub_body)} ₽
        </dd>
        <dt className="text-tg-hint">Вознаграждение</dt>
        <dd className="text-right tabular-nums">
          {fmtMoney(data.balance.rub_commission)} ₽
        </dd>
        <dt className="text-tg-hint font-medium">Итого ожидается</dt>
        <dd className="text-right tabular-nums font-semibold">
          {fmtMoney(data.balance.expected)} ₽
        </dd>
      </dl>

      {data.inflows.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader">
            Поступления
          </div>
          <ul className="text-xs mt-1 space-y-0.5">
            {data.inflows.map((i) => (
              <li key={i.id} className="flex items-center gap-2">
                <span className="text-tg-hint flex-1 truncate">
                  {fmtDT(i.received_at)}
                  {i.note ? ` · ${i.note}` : ""}
                </span>
                <span
                  className="tabular-nums"
                  style={i.amount < 0 ? { color: "#d97706" } : undefined}
                >
                  {fmtMoney(i.amount)} ₽
                </span>
                {canRecord && (
                  <button
                    onClick={() => removeInflow(i.id)}
                    className="text-tg-hint px-1 leading-none"
                    aria-label="Удалить"
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <ErrorBox text={error} />}

      {canRecord && !data.balance.fully_covered && (
        <div className="flex gap-2">
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            placeholder={`Сумма ₽ (остаток ${fmtMoney(
              data.balance.debit,
            )})`}
            className="flex-1 px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          />
          <button
            disabled={busy}
            onClick={record}
            className="px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
          >
            Записать
          </button>
        </div>
      )}
      {data.balance.fully_covered && (
        <div className="text-xs" style={{ color: "#16a34a" }}>
          ✓ Поступление собрано полностью.
        </div>
      )}
    </div>
  );
}

function DebtorsView() {
  const [rows, setRows] = useState<DebtorRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = async () => {
    setError(null);
    try {
      setRows(await api.get<DebtorRow[]>("/api/rub_inflows/debtors"));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <PullToRefresh onRefresh={load}>
      <div className="space-y-2">
        <div className="text-xs text-tg-hint">
          Контрагенты с непогашенными расчётами по рублям, по убыванию
          дебиторки.
        </div>
        {error && <ErrorBox text={error} />}
        {!rows && !error && <SkeletonRows rows={5} />}
        {rows && rows.length === 0 && (
          <EmptyState icon="✅" text="Дебиторки и кредиторки нет." />
        )}
        {rows &&
          rows.map((r) => (
            <div
              key={r.counterparty_id ?? "none"}
              className="bg-tg-sectionBg rounded-xl overflow-hidden"
            >
              <button
                onClick={() =>
                  setOpenId(
                    openId === r.counterparty_id
                      ? null
                      : r.counterparty_id,
                  )
                }
                className="w-full text-left px-4 py-3 text-sm"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium">
                    {r.counterparty_name || "—"}
                  </span>
                  <span
                    className="tabular-nums font-semibold"
                    style={{ color: "#dc2626" }}
                  >
                    {fmtMoney(r.total_debit)} ₽
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-tg-hint">
                  {r.deal_count} заявк. с дебиторкой
                  {r.total_credit > 0 &&
                    ` · переплата ${fmtMoney(r.total_credit)} ₽`}
                </div>
              </button>
              {openId === r.counterparty_id &&
                r.counterparty_id != null && (
                  <DebtorDetail cpId={r.counterparty_id} />
                )}
            </div>
          ))}
      </div>
    </PullToRefresh>
  );
}

function DebtorDetail({ cpId }: { cpId: number }) {
  const [deals, setDeals] = useState<
    Array<{
      deal_id: number;
      expected: number;
      received: number;
      debit: number;
      credit: number;
    }> | null
  >(null);

  useEffect(() => {
    api
      .get<{
        deals: Array<{
          deal_id: number;
          expected: number;
          received: number;
          debit: number;
          credit: number;
        }>;
      }>(`/api/rub_inflows/counterparty/${cpId}`)
      .then((r) => setDeals(r.deals))
      .catch(() => setDeals([]));
  }, [cpId]);

  if (!deals) {
    return (
      <div className="px-4 pb-3 text-xs text-tg-hint">Загрузка…</div>
    );
  }
  return (
    <ul className="px-4 pb-3 border-t border-tg-secondaryBg pt-2 text-xs space-y-0.5">
      {deals.map((d) => (
        <li key={d.deal_id} className="flex justify-between gap-2">
          <span className="text-tg-hint">№{d.deal_id}</span>
          <span className="tabular-nums">
            {fmtMoney(d.received)} / {fmtMoney(d.expected)} ₽
            {d.debit > 0 && (
              <span style={{ color: "#dc2626" }}>
                {" "}
                −{fmtMoney(d.debit)}
              </span>
            )}
            {d.credit > 0 && (
              <span style={{ color: "#d97706" }}>
                {" "}
                +{fmtMoney(d.credit)}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function JournalView() {
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [rows, setRows] = useState<JournalRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setError(null);
    setRows(null);
    try {
      const r = await api.get<{ total: number; rows: JournalRow[] }>(
        `/api/rub_inflows/journal?from=${from}&to=${to}`,
      );
      setRows(r.rows);
      setTotal(r.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      await api.download(
        "rub_inflows_xlsx",
        { from, to },
        `rub_inflows_${from}_${to}.xlsx`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-end text-xs">
        <label className="flex-1">
          <div className="text-tg-hint mb-0.5">С</div>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-full px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text"
          />
        </label>
        <label className="flex-1">
          <div className="text-tg-hint mb-0.5">По</div>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-full px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text"
          />
        </label>
        <button
          onClick={load}
          className="px-3 py-1.5 rounded-lg bg-tg-button text-tg-buttonText"
        >
          Показать
        </button>
      </div>

      {error && <ErrorBox text={error} />}

      {rows && (
        <div className="flex items-center justify-between">
          <div className="text-sm">
            Итого:{" "}
            <span className="font-semibold tabular-nums">
              {fmtMoney(total)} ₽
            </span>{" "}
            <span className="text-tg-hint">({rows.length})</span>
          </div>
          <button
            disabled={exporting || rows.length === 0}
            onClick={exportXlsx}
            className="px-3 py-1.5 rounded-lg bg-tg-secondaryBg text-sm disabled:opacity-50"
          >
            {exporting ? "…" : "📥 Excel"}
          </button>
        </div>
      )}

      {!rows && !error && <SkeletonRows rows={6} />}
      {rows && rows.length === 0 && (
        <EmptyState icon="📭" text="За период поступлений нет." />
      )}
      {rows &&
        rows.map((r) => (
          <div
            key={r.id}
            className="bg-tg-sectionBg rounded-xl px-4 py-2.5 text-sm"
          >
            <div className="flex justify-between gap-2">
              <span className="font-medium">
                №{r.deal_id} · {r.counterparty_name || "—"}
              </span>
              <span
                className="tabular-nums font-semibold"
                style={r.amount < 0 ? { color: "#d97706" } : undefined}
              >
                {fmtMoney(r.amount)} ₽
              </span>
            </div>
            <div className="text-xs text-tg-hint mt-0.5">
              {fmtDT(r.received_at)}
              {r.recorded_by_name ? ` · ${r.recorded_by_name}` : ""}
              {r.note ? ` · ${r.note}` : ""}
            </div>
          </div>
        ))}
    </div>
  );
}
